import clsx from 'classnames'
import * as React from 'react'
import { FormattedDateTimeRange, FormattedMessage } from 'react-intl'
import FormattedPrice from '@components/FormattedPrice'
import styles from '@components/PriceInfo/Price.module.scss'
import { PriceExplanationMark } from '@components/PriceInfo/PriceExplanationMark'
import Typography from '@components/Typography'
import {
  getAllDiscounts,
  getAllPromotions,
  getBasePrice,
  getKeyLang,
  getPriceOrNull,
  getPromotionsTotal,
  getTotalDiscountedPrice,
} from '../../helpers'
import { useIsChildrenOrPets } from '../../hooks/useIsChildrenOrPets'
import { useBookingFormStore } from '../../providers/BookingFormStoreProvider'
import { IPrice, IPriceBreakdownDiscount, IPromotion } from '../../store/data'

interface PriceBreakdownProps {
  data: IPrice | null
}

// Parse a promotion's "YYYY-MM-DD" date as a local calendar day (no time), so it renders as
// the same date in every timezone — the API dates are calendar-only (UTC-anchored).
const parsePromotionDate = (iso: string): Date => {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export const PriceBreakdown = ({ data }: PriceBreakdownProps) => {
  const language = useBookingFormStore((state) => state.language)
  const isChildrenOrPets = useIsChildrenOrPets()

  // Loyalty programs and promotions are DISTINCT discount kinds — each gets its own section
  // so a promotion never reads as a loyalty program. Loyalty is a negative sum; promotions a
  // positive sum of amounts (negated here to display as a deduction). Each section renders
  // only when it has entries.
  const loyaltyLines: IPriceBreakdownDiscount[] = data ? getAllDiscounts(data, language) : []
  const promotionLines: IPromotion[] = data ? getAllPromotions(data, language) : []
  const loyaltyDiscount = data ? getTotalDiscountedPrice(data, language) : 0
  const promotionsDiscount = data ? -getPromotionsTotal(data, language) : 0
  const totalAmount = data ? getPriceOrNull(data, 'amount', language) : null

  return (
    <div className={styles.rootPriceBreakDown}>
      <div className="w-full flex flex-col gap-2 py-2">
        <div className={clsx(styles.rootPriceBreakDownRow)}>
          <Typography className="text-muted">
            <FormattedMessage defaultMessage="Base Price" description="Label for base price summary" />
            :
          </Typography>
          <div className="font-medium">
            <FormattedPrice fractionDigits={2} value={data ? getBasePrice(data, language) : 0} />
          </div>
        </div>

        {loyaltyLines.length > 0 && (
          <div className={styles.rootPriceDiscountBreakDown}>
            <div className={styles.rootPriceBreakDownRow}>
              <Typography className="text-muted">
                <FormattedMessage
                  defaultMessage="Loyalty programs"
                  description="Label for the loyalty programs section in the price breakdown"
                />
                :
              </Typography>
              <div className="font-medium">
                <FormattedPrice fractionDigits={2} value={loyaltyDiscount} />
              </div>
            </div>
            {loyaltyLines.map((discount: IPriceBreakdownDiscount) => {
              const {
                guest,
                loyalty_program: { id: loyaltyProgramId, name },
                price_discount,
              } = discount

              return (
                <div
                  className={clsx(styles.rootPriceBreakDownRow, styles.rootPriceBreakDownDiscountRow)}
                  key={loyaltyProgramId}
                >
                  <Typography
                    className="font-normal text-muted uppercase"
                    component="span"
                    variant="extra-small"
                  >
                    {name[getKeyLang(language)]}
                    {' - '}
                    <FormattedMessage
                      defaultMessage="{count}st Guest"
                      description="Count guest in guests"
                      values={{ count: guest }}
                    />
                  </Typography>
                  <Typography
                    className="font-normal text-muted"
                    component="span"
                    variant="extra-small"
                  >
                    <FormattedPrice fractionDigits={2} value={-price_discount} />
                  </Typography>
                </div>
              )
            })}
          </div>
        )}

        {promotionLines.length > 0 && (
          <div className={styles.rootPriceDiscountBreakDown}>
            <div className={styles.rootPriceBreakDownRow}>
              <Typography className="text-muted">
                <FormattedMessage
                  defaultMessage="Discounts"
                  description="Label for the discount promotions section in the price breakdown"
                />
                :
              </Typography>
              <div className="font-medium">
                <FormattedPrice fractionDigits={2} value={promotionsDiscount} />
              </div>
            </div>
            {promotionLines.map((promo: IPromotion, index: number) => (
              <div className={styles.rootPriceBreakDownDiscountRow} key={`promo-${index}`}>
                <div className={styles.rootPriceBreakDownRow}>
                  <Typography
                    className="font-normal text-muted uppercase"
                    component="span"
                    variant="extra-small"
                  >
                    {promo.name[getKeyLang(language)] || promo.name.cs_CZ}
                    {` (−${promo.percentage} %)`}
                  </Typography>
                  <Typography
                    className="font-normal text-muted"
                    component="span"
                    variant="extra-small"
                  >
                    <FormattedPrice fractionDigits={2} value={-promo.amount} />
                  </Typography>
                </div>
                <Typography
                  className="font-normal text-muted opacity-70"
                  component="div"
                  variant="extra-small"
                >
                  <FormattedMessage
                    defaultMessage="valid {range} ({nights, plural, one {# night} other {# nights}})"
                    description="Promotion validity date range and night count in the price breakdown"
                    values={{
                      nights: promo.nights,
                      range: (
                        <FormattedDateTimeRange
                          day="2-digit"
                          from={parsePromotionDate(promo.valid_from)}
                          month="2-digit"
                          to={parsePromotionDate(promo.valid_to)}
                          year="numeric"
                        />
                      ),
                    }}
                  />
                </Typography>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className={clsx(styles.rootPriceBreakDownRow, 'mt-5')}>
        <Typography className={styles.rootPrice} component="span" variant="category">
          <FormattedMessage defaultMessage="Total" description="Label for total price summary" />
        </Typography>
        <div className="flex flex-col items-end">
          <Typography className={clsx(styles.rootPrice)} component="span" variant="category">
            {totalAmount !== null ? (
              <>
                <FormattedPrice fractionDigits={2} value={totalAmount} />
                {isChildrenOrPets && <PriceExplanationMark />}
              </>
            ) : (
              '-'
            )}
          </Typography>
          <Typography className={clsx(styles.rootDescription, 'text-muted')} component="span">
            <FormattedMessage defaultMessage="(incl. VAT)" description="Text in Price component" />
          </Typography>
        </div>
      </div>
    </div>
  )
}
